import { useEffect } from 'react';

interface ShortcutRow {
  keys: string[];
  action: string;
  description: string;
}

const SHORTCUT_GROUPS: { title: string; icon: string; rows: ShortcutRow[] }[] = [
  {
    title: 'Generation',
    icon: 'ri-sparkling-fill',
    rows: [
      { keys: ['Ctrl', 'Enter'], action: 'Generate Image', description: 'Start generating with the current prompt & settings' },
      { keys: ['Ctrl', 'Shift', 'Enter'], action: 'Regenerate', description: 'Regenerate the last image with the same settings' },
    ],
  },
  {
    title: 'Prompt',
    icon: 'ri-edit-2-line',
    rows: [
      { keys: ['Ctrl', 'E'], action: '✨ Enhance Prompt', description: 'AI-upgrade your prompt with style-specific detail' },
      { keys: ['Ctrl', 'L'], action: 'Clear Prompt', description: 'Clear the prompt textarea' },
      { keys: ['Ctrl', 'Z'], action: 'Undo Enhance', description: 'Revert prompt to pre-enhancement version' },
    ],
  },
  {
    title: 'View & Compare',
    icon: 'ri-layout-column-line',
    rows: [
      { keys: ['Ctrl', 'K'], action: 'Compare Variations', description: 'Generate a variation and open comparison view' },
      { keys: ['Ctrl', 'H'], action: 'Open History', description: 'Open the My Generations history panel' },
      { keys: ['Escape'], action: 'Close Panel', description: 'Close any open modal or panel' },
    ],
  },
  {
    title: 'Export',
    icon: 'ri-share-line',
    rows: [
      { keys: ['Ctrl', 'S'], action: 'Share & Export', description: 'Open the Share & Export modal' },
      { keys: ['Ctrl', 'D'], action: 'Download Image', description: 'Quick-download the generated image' },
    ],
  },
];

interface KeyboardShortcutsLegendProps {
  isOpen: boolean;
  onClose: () => void;
  darkMode: boolean;
}

function KbdKey({ children }: { children: string }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[28px] h-6 px-1.5 rounded-md text-[11px] font-bold border font-mono bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 shadow-[0_1px_0_1px_rgba(0,0,0,0.08)] dark:shadow-[0_1px_0_1px_rgba(0,0,0,0.4)]">
      {children}
    </kbd>
  );
}

export default function KeyboardShortcutsLegend({ isOpen, onClose, darkMode }: KeyboardShortcutsLegendProps) {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none`}>
        <div
          className={`w-full max-w-2xl max-h-[85vh] rounded-2xl shadow-2xl overflow-hidden pointer-events-auto flex flex-col ${
            darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'
          }`}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 bg-gradient-to-r from-purple-500 to-pink-600">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-xl">
                <i className="ri-keyboard-line text-white text-lg"></i>
              </div>
              <div>
                <h3 className="text-white font-bold text-base">Keyboard Shortcuts</h3>
                <p className="text-white/70 text-xs">Speed up your workflow</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/20 text-white transition-colors"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* Body */}
          <div className="overflow-y-auto flex-1 p-6 space-y-6">
            {SHORTCUT_GROUPS.map((group) => (
              <div key={group.title}>
                <div className="flex items-center gap-2 mb-3">
                  <i className={`${group.icon} text-sm text-purple-500`}></i>
                  <h4 className={`text-xs font-bold uppercase tracking-widest ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {group.title}
                  </h4>
                </div>
                <div className={`rounded-xl overflow-hidden border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  {group.rows.map((row, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-4 px-4 py-3 ${
                        i < group.rows.length - 1 ? `border-b ${darkMode ? 'border-gray-700/60' : 'border-gray-100'}` : ''
                      } ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50'} transition-colors`}
                    >
                      {/* Keys */}
                      <div className="flex items-center gap-1 flex-shrink-0 w-40">
                        {row.keys.map((k, ki) => (
                          <>
                            <KbdKey key={`key-${ki}`}>{k}</KbdKey>
                            {ki < row.keys.length - 1 && (
                              <span key={`plus-${ki}`} className={`text-xs mx-0.5 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}>+</span>
                            )}
                          </>
                        ))}
                      </div>
                      {/* Action + desc */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{row.action}</p>
                        <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{row.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className={`px-6 py-4 border-t flex items-center gap-2 flex-shrink-0 ${darkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-100 bg-gray-50'}`}>
            <KbdKey>?</KbdKey>
            <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Press <strong className={darkMode ? 'text-gray-300' : 'text-gray-600'}>?</strong> anywhere to open / close this legend
            </span>
            <button
              onClick={onClose}
              className="ml-auto px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity whitespace-nowrap"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
